import React, { PropsWithChildren } from 'react';
import {
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Navbar,
  NavbarBrand,
  NavbarToggler,
  UncontrolledDropdown
} from 'reactstrap';
import Icon from 'components/Icon';
import { NavLink as NavLinkRouter, useLocation } from 'react-router-dom';
import AlertPopups from 'components/AlertPopups';
import MediaUploadProvider from 'contexts/mediaUpload';
import {
  AdminCustomerProvider,
  useAdminCustomer
} from 'contexts/adminCustomer';
import NotificationButton from 'components/NotificationButton';
import CustomerSelector from 'components/CustomerSelector';
import classNames from 'classnames';
import useControllerDashboardLayout from './_presenters/useControllerDashboardLayout/useControllerDashboardLayout';
import iconDown from '../../assets/images/More.png';
import Sidebar from 'layouts/DashboardLayout/Sidebar';
import AdminCustomerRequired from 'components/AdminCustomerRequired';

type LayoutProps = PropsWithChildren<{
  routes: { [key: string]: any };
  children: React.ReactNode;
}>;

const DashboardLayoutGuard = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const { isAdmin, hasCustomerContext } = useAdminCustomer();

  const isAdminRoute = location.pathname.startsWith('/admin');

  if (isAdmin && !hasCustomerContext && !isAdminRoute) {
    return <AdminCustomerRequired />;
  }

  return <>{children}</>;
};

const DashboardLayout = ({ children, routes }: LayoutProps) => {
  const {
    user,
    onLogoutHandler,
    isOpen,
    toggle,
    dropdownOpen,
    toggleMenu
  } = useControllerDashboardLayout();

  return (
    <AdminCustomerProvider>
      <MediaUploadProvider>
        <div className="overflow-x-hidden overflow-y-hidden vw-100 vh-100">
          <Navbar
            color="white"
            fixed="top"
            container="fluid"
            light
            className={classNames(
              'sticky-top d-flex',
              'justify-content-between',
              'border',
              'border-top-0',
              'border-bottom-1',
              'border-gray-200',
              isOpen ? 'closed' : 'navbar'
            )}
          >
            {isOpen ? (
              <NavbarToggler className="border-0" onClick={toggle}>
                {' '}
                <Icon icon="list" className="text-primary" />
              </NavbarToggler>
            ) : (
              <NavbarToggler className="border-0" onClick={toggle}>
                {' '}
                <Icon icon="chevron-left" className="text-primary" />
              </NavbarToggler>
            )}
            <NavbarBrand tag="div" className="d-flex align-items-center">
              <CustomerSelector />
              <NotificationButton />
              <UncontrolledDropdown
                isOpen={dropdownOpen}
                toggle={toggleMenu}
                direction="down"
                data-bs-popper="static"
              >
                <DropdownToggle
                  color="none"
                  className="d-flex align-items-center border-0"
                >
                  {user?.picture ? (
                    <>
                      <img
                        width="44px"
                        height="44px"
                        src={user.picture}
                        alt="User"
                        className="rounded-circle mx-auto"
                      />
                      {user && (
                        <span className="d-flex flex-column justify-content-center fs-6 text-gray-800 bold ms-3">
                          {user.firstName} {user.lastName}
                          <small className="text-gray-700 semibold">
                            {user.role === 'CUSTOMER'
                              ? 'Usuário'
                              : 'Administrador'}
                          </small>
                        </span>
                      )}

                      <img
                        alt="iconDowm"
                        src={iconDown}
                        className="text-primary ms-4"
                      />
                    </>
                  ) : (
                    <>
                      <Icon
                        icon="person-circle"
                        className="text-secondary"
                        width={44}
                        height={44}
                      />
                      {user && (
                        <span className="d-flex flex-column justify-content-center fs-6 text-gray-800 bold ms-3">
                          {user.firstName} {user.lastName}
                          <small className="text-gray-700 semibold">
                            {user.role === 'CUSTOMER' ? 'Usuário' : 'Admin'}
                          </small>
                        </span>
                      )}
                      <img
                        alt="iconDowm"
                        src={iconDown}
                        className="text-primary ms-4"
                      />
                    </>
                  )}
                </DropdownToggle>
                <DropdownMenu end>
                  <DropdownItem
                    to={routes.USER_PROFILE.EDIT}
                    tag={NavLinkRouter}
                  >
                    Sua Conta
                  </DropdownItem>
                  <DropdownItem onClick={onLogoutHandler}>
                    Sair do Sistema
                  </DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
            </NavbarBrand>
          </Navbar>
          <div className="d-flex flex-row">
            <Sidebar
              isOpen={!isOpen}
              toggleSidebar={toggle}
              routes={routes}
              data-bs-popper="static"
            />
            <div className="flex-fill bg-gray-100 container-page overflow-y-scroll">
              <Container fluid className="position-relative">
                <DashboardLayoutGuard>{children}</DashboardLayoutGuard>
              </Container>
            </div>
          </div>
          <AlertPopups />
        </div>
      </MediaUploadProvider>
    </AdminCustomerProvider>
  );
};

export default DashboardLayout;
